import { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import dayjs from "dayjs";

import {
  AccountHistoryRecord,
  AdjustmentTypeEnum,
  BalanceTypeEnum,
} from "helpers/types";
import { getAdjustmentTypeColor } from "styles/Strategy.styles";
import { formatAdjustmentType } from "helpers";
import BalanceTypeToggle from "components/ui/BalanceTypeToggle";
import { useAccountHistoryGeneration } from "hooks/useAccountHistoryGeneration";
import { useAccountHistoryStatus } from "./useAccountHistoryStatus";

interface ReportAccountHistoryProps {
  accountId: number | null;
  strategyId: number | null;
}

interface HistoryEntryCardProps {
  entry: AccountHistoryRecord;
}

const GENERATE_LABEL: Record<BalanceTypeEnum, string> = {
  [BalanceTypeEnum.TRADE_AMOUNT]: "Generate trade amount history",
  [BalanceTypeEnum.PORTFOLIO]: "Generate portfolio history",
};

const HistoryEntryCard = ({ entry }: HistoryEntryCardProps) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      border: 1,
      borderColor: "primary.light",
      borderRadius: 1,
      p: 1,
    }}
  >
    <Typography variant="caption" color="primary.main" fontWeight={600}>
      {entry.adjustment_date
        ? dayjs(entry.adjustment_date).format("MM/DD/YYYY")
        : "N/A"}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={700}
      color={getAdjustmentTypeColor(entry.adjustment_type as AdjustmentTypeEnum)}
    >
      {formatAdjustmentType(entry.adjustment_type)}
    </Typography>
    <Typography variant="body2">Amount: {entry.adjustment_amount}</Typography>
    <Typography variant="caption" color="text.secondary">
      Balance: {entry.unit_balance_after_adjustment}
    </Typography>
  </Box>
);

const ReportAccountHistory = ({
  accountId,
  strategyId,
}: ReportAccountHistoryProps) => {
  const [balanceType, setBalanceType] = useState<BalanceTypeEnum>(
    BalanceTypeEnum.TRADE_AMOUNT,
  );
  const status = useAccountHistoryStatus(accountId, strategyId);
  const {
    generate,
    isGeneratingTradeAmount,
    isGeneratingPortfolio,
  } = useAccountHistoryGeneration(accountId, strategyId);

  if (!accountId || !strategyId) return null;

  const entries = status.recentByType[balanceType];
  const isGenerating = {
    [BalanceTypeEnum.TRADE_AMOUNT]: isGeneratingTradeAmount,
    [BalanceTypeEnum.PORTFOLIO]: isGeneratingPortfolio,
  }[balanceType];

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">
          Recent positions in history
        </Typography>
        <BalanceTypeToggle value={balanceType} onChange={setBalanceType} />
      </Box>

      {status.isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading history…
          </Typography>
        </Box>
      ) : entries.length > 0 ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          {entries.map((entry) => (
            <HistoryEntryCard key={entry.id} entry={entry} />
          ))}
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          disabled={isGenerating}
          onClick={() => generate(balanceType)}
          startIcon={isGenerating ? <CircularProgress size={16} /> : null}
        >
          {isGenerating ? "Generating…" : GENERATE_LABEL[balanceType]}
        </Button>
      )}
    </Box>
  );
};

export default ReportAccountHistory;
